// /app/frontend/src/pages/RoomsPage.jsx
// Rooms hub - Lists all community rooms with consistent, premium styling
//
// VISIBILITY RULES (enforced by backend):
// - hub_only rooms (e.g., her_space_private) are never shown
// - women_only rooms (e.g., her_space) only shown to users with gender="woman"
// - The backend filters based on user gender, this component renders whatever is returned

import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, MessageCircle, Sparkles, ChevronRight } from "lucide-react";
import { listRooms } from "../api/rooms";
import { GlobalNav } from "../components/GlobalNav";
import { AuthContext } from "../App";

// Room-specific accent colors for visual variety
const ROOM_ACCENTS = {
  'meditation': { bg: 'bg-indigo-50 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-200 dark:border-indigo-800/50' },
  'her_space': { bg: 'bg-rose-50 dark:bg-rose-900/30', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-800/50' },
  'mens_work': { bg: 'bg-slate-100 dark:bg-slate-800/50', text: 'text-slate-600 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700/50' },
  'the_universe': { bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800/50' },
};

const DEFAULT_ACCENT = { bg: 'bg-gray-50 dark:bg-slate-800/50', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-slate-700/50' };

export default function RoomsPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await listRooms();
        if (mounted) setRooms(Array.isArray(r) ? r : []);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Defense-in-depth: Client-side filtering for visibility rules
  // Backend handles this, but we filter again just in case
  const filteredRooms = useMemo(() => {
    const userGender = user?.gender?.toLowerCase() || "";
    const isWoman = userGender === "woman";
    
    return rooms.filter(room => {
      // Never show hub_only rooms in global list
      if (room.hub_only === true || room.visibility === "hub_only") {
        return false;
      }
      
      // Women-only rooms only visible to women
      if (room.visibility === "women_only" && !isWoman) {
        return false;
      }
      
      return true;
    });
  }, [rooms, user]);

  return (
    <div className="min-h-screen surface-page">
      <GlobalNav />
      
      <div className="mx-auto w-full max-w-3xl px-4 py-6 pb-24">
        {/* Header */}
        <header className="mb-8">
          <button
            type="button"
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition mb-4"
            onClick={() => navigate("/dashboard")}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Rooms
            </h1>
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
              Community
            </span>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Calm spaces for real conversation. Join a room that resonates with you.
          </p>
        </header>

        {/* Rooms List */}
        <section className="space-y-4">
          {loading ? (
            <div className="surface-card rounded-2xl p-8 text-center">
              <div className="animate-pulse flex flex-col items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-slate-700" />
                <div className="h-4 w-32 rounded bg-gray-200 dark:bg-slate-700" />
              </div>
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className="surface-card rounded-2xl p-8 text-center">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                No rooms available yet. Check back soon!
              </p>
            </div>
          ) : (
            filteredRooms.map((r) => {
              const accent = ROOM_ACCENTS[r.slug] || ROOM_ACCENTS[r.room_key] || DEFAULT_ACCENT;
              
              return (
                <button
                  key={r.id}
                  className={`w-full surface-card rounded-2xl p-5 text-left shadow-sm hover:shadow-md border ${accent.border} transition-all group`}
                  onClick={() => navigate(`/rooms/${r.slug}`)}
                  data-testid={`room-card-${r.slug}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Room Icon */}
                    <div className={`w-12 h-12 rounded-xl ${accent.bg} flex items-center justify-center flex-shrink-0`}>
                      <Users className={`w-6 h-6 ${accent.text}`} />
                    </div>
                    
                    {/* Room Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base font-semibold text-gray-900 dark:text-white">
                          {r.name}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                        {r.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
                        <Sparkles className="w-3 h-3" />
                        <span>Weekly topics • Real talk</span>
                      </div>
                    </div>
                    
                    {/* Arrow */}
                    <div className="flex-shrink-0 self-center">
                      <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition" />
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </section>

        {/* Info Card */}
        <section className="mt-8">
          <div className="surface-card rounded-2xl p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  About Rooms
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Rooms are calm community spaces where you can ask questions, share thoughts, 
                  and connect with others who understand your journey. Each room has a daily 
                  topic to spark meaningful conversation.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
